import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import BrandedListingContent from '../../Map/components/BrandedShareChrome/BrandedListingContent';
import ListingVideoEmbeds from '../../Map/components/DockPanel/Tabs/ListingVideoEmbeds';
import ListingAttachments from '../../Map/components/DockPanel/Tabs/ListingAttachments';
import { colors, typography } from '../../styles/theme';
import { buildCadBrandedPresentation } from '../utils/cadBrandedListing';
import {
  getCadParcelAttachments,
  getCadParcelVideos,
} from '../utils/cadParcelDisplay';
import CadStockStatusLabel from './CadStockStatusLabel';
import ShowroomParcelReadout from './ShowroomParcelReadout';

const CAD_BRANDED_OMIT_GROUPS = ['presentacion', 'estado', 'precio', 'fotos', 'videos', 'adjuntos'];

const SECTION_TITLE_SX = {
  color: colors.textPanelTitle,
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  fontFamily: typography.fontFamily.primary,
  display: 'block',
  mb: 1.25,
  lineHeight: '16px',
};

/**
 * Cuerpo branded de la ficha CAD (comercial + campos de lote).
 * Sin PanelShell: lo usa tanto `/#/lista/:id` como el visitante in-panel.
 * No reusa DockPanel: el lote CAD no es un `lot_id`.
 * Videos/adjuntos siempre desde properties del feature (sin toggle).
 */
export default function BrandedCadParcelDetail({
  feature,
  accentColor,
  inquiry = null,
}) {
  const presentation = useMemo(
    () => buildCadBrandedPresentation(feature),
    [feature],
  );
  const videos = useMemo(() => getCadParcelVideos(feature), [feature]);
  const attachments = useMemo(() => getCadParcelAttachments(feature), [feature]);

  const hasCommercialLead = Boolean(
    presentation
    && (
      presentation.title
      || presentation.description
      || presentation.hero
      || presentation.metas.length > 0
      || presentation.images.length > 0
      || videos.length > 0
      || attachments.length > 0
    ),
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <ShowroomParcelReadout
        feature={feature}
        omitGroups={CAD_BRANDED_OMIT_GROUPS}
        lead={hasCommercialLead ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {presentation ? (
              <BrandedListingContent
                title={presentation.title}
                description={presentation.description}
                descriptionClamp={false}
                hero={presentation.hero}
                metas={presentation.metas.map((meta) => (
                  meta.key === 'stock'
                    ? { ...meta, value: <CadStockStatusLabel status={feature?.properties?.stock_status} /> }
                    : meta
                ))}
                images={presentation.images}
                accentColor={accentColor}
              />
            ) : null}
            {videos.length > 0 && (
              <Box>
                <Typography sx={SECTION_TITLE_SX}>Videos</Typography>
                <ListingVideoEmbeds videos={videos} />
              </Box>
            )}
            {attachments.length > 0 && (
              <Box>
                <Typography sx={SECTION_TITLE_SX}>Adjuntos</Typography>
                <ListingAttachments attachments={attachments} />
              </Box>
            )}
          </Box>
        ) : null}
      />
      {inquiry}
    </Box>
  );
}
